<template>
  <div ref="groupRoot" class="sp-tile-group" :class="classModifiers">
    <slot />
  </div>
</template>

<script setup>
import { computed, useTemplateRef } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useEqualizeHeights } from "#ce/composables/useEqualizeHeights";
import { toBoolean } from "#ce/utils/props";
import { props as propDefs } from "./propsGroup";

const props = defineProps(propDefs);

const { isSmScreen, isMdScreen, breakpointModifiers } = useBreakpoints();
const groupRoot = useTemplateRef("groupRoot");

const effectiveItemsPerRow = computed(() => {
  if (isSmScreen.value) {
    return "auto";
  }
  if (isMdScreen.value && props.itemsPerRowMd !== undefined) {
    return String(props.itemsPerRowMd);
  }
  return String(props.itemsPerRow);
});
const isFixedItemsPerRow = computed(() => effectiveItemsPerRow.value !== "auto");
const isConnectedMode = computed(() => toBoolean(props.connectOnSm) && isSmScreen.value);
const effectiveEqualize = computed(() => {
  if (isConnectedMode.value) {
    return false;
  }
  if (isSmScreen.value && props.equalizeHeightsSm !== undefined) {
    return toBoolean(props.equalizeHeightsSm);
  }
  return toBoolean(props.equalizeHeights);
});
const isScrollableOnSm = computed(
  () => toBoolean(props.scrollOnSm) && isSmScreen.value && !toBoolean(props.connectOnSm),
);
const { equalizedHeight } = useEqualizeHeights(groupRoot, "SP-TILE", effectiveEqualize, [isSmScreen]);

const classModifiers = computed(() => [
  ...breakpointModifiers.value,
  { "--is-scroll-sm": isScrollableOnSm.value },
  { "--connected": isConnectedMode.value },
  { "--equalize": effectiveEqualize.value },
  {
    [`--items-per-row-${effectiveItemsPerRow.value}`]: isFixedItemsPerRow.value,
  },
]);
</script>

<style>
:host {
  --gap: var(--sp-comp-tile-group-gap, var(--sp-ref-spacing-12));
  --gap-sm: var(--sp-comp-tile-group-gap-sm, var(--sp-ref-spacing-8));
  --tile-min-width: var(--sp-comp-tile-group-tile-min-width, 8rem);
  --scroll-padding: var(--sp-comp-tile-group-scroll-padding, var(--sp-ref-spacing-10));

  /* Connected mode */
  --connected-background: var(--sp-comp-tile-group-connected-background, var(--sp-sys-color-surface));
  --connected-border-radius: var(--sp-comp-tile-group-connected-border-radius, var(--sp-ref-border-radius-lg));
  --connected-box-shadow: var(--sp-comp-tile-group-connected-box-shadow, var(--sp-sys-elevation-3));
  --connected-padding-block: var(--sp-comp-tile-group-connected-padding-block, var(--sp-ref-spacing-4));
  --connected-padding-inline: var(--sp-comp-tile-group-connected-padding-inline, var(--sp-ref-spacing-6));
  --connected-item-padding-block: var(--sp-comp-tile-group-connected-item-padding-block, var(--sp-ref-spacing-6));
  --connected-item-padding-inline: var(--sp-comp-tile-group-connected-item-padding-inline, var(--sp-ref-spacing-4));
  --connected-divider-color: var(--sp-comp-tile-group-connected-divider-color, var(--sp-sys-color-outline-variant));
  --connected-divider-inset: var(--sp-comp-tile-group-connected-divider-inset, 0);

  display: block;
}
</style>

<style scoped lang="scss">
@use "@ce/shared/connected-group" as *;

.sp-tile-group {
  display: grid;
  gap: var(--gap);
  grid-template-columns: repeat(auto-fill, minmax(var(--tile-min-width), 1fr));
  justify-items: stretch;
  align-items: start;

  &.--is-sm-screen {
    gap: var(--gap-sm);
    grid-template-columns: 1fr;
  }

  &:not(.--connected).--equalize {
    ::slotted(sp-tile) {
      --sp-tile-group-min-height: v-bind(equalizedHeight);
    }
  }

  &.--is-sm-screen:not(.--connected).--equalize {
    ::slotted(sp-tile) {
      --sp-tile-group-min-height-sm: v-bind(equalizedHeight);
    }
  }

  @for $i from 1 through 6 {
    &.--items-per-row-#{$i} {
      grid-template-columns: repeat(#{$i}, 1fr);
    }
  }

  // Scrollable mode (mobile)
  &.--is-scroll-sm {
    grid-auto-flow: column;
    grid-template-columns: none;
    grid-auto-columns: minmax(var(--tile-min-width), max-content);

    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;

    padding-inline: var(--scroll-padding);
    padding-block-end: var(--sp-ref-spacing-2);
    margin-inline: calc(var(--scroll-padding) * -1);

    scroll-padding-inline: var(--scroll-padding);

    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }

    ::slotted(sp-tile) {
      scroll-snap-align: start;
      scroll-snap-stop: normal;
    }
  }

  // Connected mode (mobile)
  &.--connected {
    @include connected-group(
      sp-tile,
      $bg-var: --connected-background,
      $radius-var: --connected-border-radius,
      $shadow-var: --connected-box-shadow,
      $padding-block-var: --connected-padding-block,
      $padding-inline-var: --connected-padding-inline,
      $divider-color-var: --connected-divider-color,
      $divider-inset-var: --connected-divider-inset
    );

    ::slotted(sp-tile) {
      // Override SpTile card styling - remove individual card appearance and replace with connected mode styling
      --sp-tile-group-card-background: var(--sp-sys-color-transparent);
      --sp-tile-group-card-border: 1px solid var(--sp-sys-color-transparent);
      --sp-tile-group-card-border-radius: 0;
      --sp-tile-group-card-shadow: none;
      --sp-tile-group-card-padding-block: var(--connected-item-padding-block);
      --sp-tile-group-card-padding-inline: var(--connected-item-padding-inline);
      --sp-tile-group-card-shadow-hover: none;
      --sp-tile-group-connected-layout-direction-sm: row;
      --sp-tile-group-connected-padding-block: var(--connected-item-padding-block);
      --sp-tile-group-connected-padding-inline: var(--connected-item-padding-inline);
    }
  }
}
</style>
